import * as Sentry from '@sentry/react'
import { useEffect, useState } from 'react'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'

/** Tracker phase returned by GET /orders/status (derived from imdb_client tracker fields). */
export type OrderStatus = 'pending' | 'submitted' | 'processing' | 'complete' | 'other_status' | null

export type OrderTrackerVariant = 'active' | 'hidden'

export type OrderTrackerStep = 1 | 2 | 3 | null

export interface OrderTrackerResult {
  step: OrderTrackerStep
  variant: OrderTrackerVariant
  loading: boolean
}

type ApiResponse = { status: OrderStatus }

export function mapStatusToTracker(status: OrderStatus): Pick<OrderTrackerResult, 'step' | 'variant'> {
  switch (status) {
    case 'pending':
    case 'submitted':
      return { step: 1, variant: 'active' }
    case 'processing':
      return { step: 2, variant: 'active' }
    case 'complete':
      return { step: 3, variant: 'active' }
    case 'other_status':
      return { step: null, variant: 'hidden' }
    default:
      return { step: null, variant: 'hidden' }
  }
}

export function useOrderTracker(enabled = true): OrderTrackerResult {
  const apiClient = useFamilyPortalApiClient()
  const [result, setResult] = useState<Pick<OrderTrackerResult, 'step' | 'variant'>>({
    step: null,
    variant: 'hidden',
  })
  const [loading, setLoading] = useState(enabled)

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    apiClient
      .get('/orders/status')
      .then((res) => {
        if (cancelled || !res.ok) return undefined
        return res.json() as Promise<ApiResponse>
      })
      .then((data) => {
        if (cancelled || !data) return
        setResult(mapStatusToTracker(data.status))
      })
      .catch((err) => {
        Sentry.captureException(err, {
          tags: { operation: 'orderTracker.fetchStatus', flow: 'family-portal' },
        })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [apiClient, enabled])

  return { ...result, loading }
}
